import React, { Fragment, FC } from 'react';
import { CommodityStatusCode, MilestoneStatusCode } from '@bechtel/types';
import { Box } from '@bechtel/gds';
import { Text } from '@bechtel/gds';
import { Avatar, AvatarSize } from '@bechtel/gds';
import { CountMetadataProps, MilestoneMetadataProps, UserMetadataProps, AssociatedWeldsMetaData } from './types';
import { WeldoutList } from '../weldouts-list';
import { filter } from 'lodash';
import { useDate, useName, TextWithEllipsis, EllipsisPlacement } from '@bechtel/client-common';
import { t } from '@lingui/macro';

// NOTE: this is for things like showing who claimed a milestone
export const UserMetadata: FC<UserMetadataProps> = ({ startDate, name, endDate, imageUrl }) => {
  return (
    <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <Avatar src={imageUrl} size={AvatarSize.MEDIUM} />
      </Box>
      {/* minWidth is required to control text overflow in flexbox */}
      <Box flexDirection={{ sm: 'column' }} style={{ minWidth: 0 }}>
        <Box py={{ sm: '200' }}>
          <TextWithEllipsis size="300" weight="600" color="grey.1000" ellipsisPlacement={EllipsisPlacement.END}>
            {name}
          </TextWithEllipsis>
        </Box>
        <Box pb={{ sm: '100' }}>
          <Text size="250" color="grey.600">
            {startDate}&nbsp;
          </Text>
          {endDate && (
            <Text size="250" color="grey.600">
              - {endDate}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// NOTE: this is for things like listing the number of rejections
export const CountMetadata: FC<CountMetadataProps> = ({ label, count }) => {
  return (
    <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
      <Text color="grey.600" size="250">
        {label}: {count}
      </Text>
    </Box>
  );
};

export const MilestoneMetadata: FC<MilestoneMetadataProps> = ({ milestone, status }) => {
  const { formatISODate } = useDate();
  const { getFullName } = useName();
  if (
    (status === MilestoneStatusCode.CLAIMED || status === MilestoneStatusCode.APPROVED) &&
    milestone.currentStatus &&
    milestone.currentStatus.createdBy
  )
    return (
      <UserMetadata
        name={getFullName(milestone.currentStatus.createdBy.firstName, milestone.currentStatus.createdBy.lastName)}
        imageUrl={milestone.currentStatus.createdBy.imageUrl}
        startDate={formatISODate(milestone.currentStatus.createdAt)}
      />
    );

  //TODO : Checking for Planned Start date as backend is currently sending planned status for milestones that are not claimed/planned.
  //This results in showing the name of the user who unclaims the milestone.
  if (
    status === MilestoneStatusCode.PLANNED &&
    milestone.currentStatus &&
    milestone.plannedStartDate &&
    milestone.currentStatus.createdBy
  )
    return (
      <UserMetadata
        name={getFullName(milestone.currentStatus.createdBy.firstName, milestone.currentStatus.createdBy.lastName)}
        imageUrl={milestone.currentStatus.createdBy.imageUrl}
        startDate={formatISODate(milestone.plannedStartDate)}
        endDate={formatISODate(milestone.plannedEndDate)}
      />
    );

  if (status === MilestoneStatusCode.REJECTED)
    return <CountMetadata label={t`Rejection Count`} count={milestone.timesRejected} />;

  return null;
};

export const AssociatedWeldsMetadata: FC<AssociatedWeldsMetaData> = ({ weldStatuses }) => {
  const weldCompletedCount = filter(weldStatuses, (weld) => weld?.status?.status === CommodityStatusCode.VT_APPROVED)
    .length;
  return (
    <Fragment>
      <CountMetadata label={t`Weldouts Complete`} count={`${weldCompletedCount}/${weldStatuses.length}`} />
      <WeldoutList weldStatuses={weldStatuses} />
    </Fragment>
  );
};
